import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatMoney, formatDate, toISODate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Repeat, Plus, Trash2 } from "lucide-react";

const freqLabel = { weekly: "Haftalık", monthly: "Aylık", yearly: "Yıllık" };

export default function Recurring() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    type: "expense",
    amount: "",
    currency: "TRY",
    description: "",
    frequency: "monthly",
    next_date: toISODate(new Date()),
    category_id: "",
    active: true,
  });

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([api.get("/recurring"), api.get("/categories")]);
    setItems(r.data);
    setCats(c.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const catColor = (id) => cats.find((c) => c.id === id)?.color || "#64748B";

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recurring", {
        ...form,
        amount: Number(form.amount),
        category_id: form.category_id || null,
      });
      toast.success("Abonelik eklendi");
      setForm({
        type: "expense", amount: "", currency: "TRY", description: "",
        frequency: "monthly", next_date: toISODate(new Date()), category_id: "", active: true
      });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try { await api.delete(`/recurring/${id}`); toast.success("Silindi"); load(); } catch { toast.error("Hata"); }
  };

  const toggle = async (id) => {
    try { await api.post(`/recurring/${id}/toggle`); load(); } catch { toast.error("Hata"); }
  };

  const monthlyBurn = items
    .filter((i) => i.active && i.type === "expense")
    .reduce((acc, i) => acc + Number(i.amount) * (i.frequency === "weekly" ? 4.33 : i.frequency === "yearly" ? 1 / 12 : 1), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Tekrarlayan</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Abonelikler & Düzenli Ödemeler</h1>
          <p className="text-sm text-slate-400 mt-1">Yazılım, kira, SaaS ve diğer tekrarlayan giderlerinizi yönetin.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="recurring-add-button" className="bg-indigo-500 hover:bg-indigo-600">
          <Plus className="w-4 h-4 mr-1.5" /> Yeni Abonelik
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Aktif Abonelik</div>
          <div className="font-display text-3xl font-bold mt-2">{items.filter((i) => i.active).length}</div>
        </div>
        <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Aylık Gider Yükü (TRY yakın)</div>
          <div className="font-mono-num text-3xl font-bold text-rose-400 mt-2">{formatMoney(monthlyBurn, "TRY")}</div>
        </div>
        <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Toplam Kayıt</div>
          <div className="font-display text-3xl font-bold mt-2">{items.length}</div>
        </div>
      </div>

      <div className="bg-[#131924] border border-white/5 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-white/5 flex items-center gap-2">
          <Repeat className="w-4 h-4 text-indigo-400" />
          <h2 className="font-display text-lg font-bold tracking-tight">Tüm Abonelikler</h2>
        </div>
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">Henüz tekrarlayan ödeme eklenmedi.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((i) => (
              <div key={i.id} className="p-4 flex items-center gap-4" data-testid={`recurring-${i.id}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${i.type === "income" ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
                  <Repeat className={`w-4 h-4 ${i.type === "income" ? "text-emerald-400" : "text-rose-400"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{i.description}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{ background: catColor(i.category_id) + "22", color: catColor(i.category_id) }}
                    >
                      {catName(i.category_id)}
                    </span>
                    <span className="text-[11px] text-slate-500">{freqLabel[i.frequency]}</span>
                    <span className="text-[11px] text-slate-500 font-mono-num">Sonraki: {formatDate(i.next_date)}</span>
                  </div>
                </div>
                <div className={`font-mono-num font-semibold ${i.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                  {i.type === "income" ? "+" : "-"}{formatMoney(i.amount, i.currency)}
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={i.active} onCheckedChange={() => toggle(i.id)} data-testid={`recurring-toggle-${i.id}`} />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => del(i.id)}
                    data-testid={`recurring-delete-${i.id}`}
                    className="h-8 w-8 text-slate-400 hover:text-rose-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131924] border-white/10 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Yeni Abonelik</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Açıklama</Label>
              <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="recurring-description" className="mt-2 bg-[#0F1522] border-white/10" placeholder="Örn: Netflix, AWS, Kira" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category_id: "" })}>
                  <SelectTrigger data-testid="recurring-type" className="mt-2 bg-[#0F1522] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
                    <SelectItem value="expense">Gider</SelectItem>
                    <SelectItem value="income">Gelir</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Sıklık</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="recurring-frequency" className="mt-2 bg-[#0F1522] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
                    <SelectItem value="weekly">Haftalık</SelectItem>
                    <SelectItem value="monthly">Aylık</SelectItem>
                    <SelectItem value="yearly">Yıllık</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Tutar</Label>
                <Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="recurring-amount" className="mt-2 bg-[#0F1522] border-white/10 font-mono-num" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">PB</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="recurring-currency" className="mt-2 bg-[#0F1522] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
                    <SelectItem value="TRY">₺</SelectItem>
                    <SelectItem value="USD">$</SelectItem>
                    <SelectItem value="EUR">€</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Sonraki Tarih</Label>
                <Input required type="date" value={form.next_date} onChange={(e) => setForm({ ...form, next_date: e.target.value })} data-testid="recurring-nextdate" className="mt-2 bg-[#0F1522] border-white/10" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Kategori</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="recurring-category" className="mt-2 bg-[#0F1522] border-white/10"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent className="bg-[#131924] border-white/10 text-slate-100 max-h-60">
                    {cats.filter((c) => c.kind === form.type).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">İptal</Button>
              <Button type="submit" data-testid="recurring-save" className="bg-indigo-500 hover:bg-indigo-600">Kaydet</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
